import React, { useEffect, useRef, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { onOpenCopilot } from '../../scripts/copilot.controller';
import { emitDocumentAssigned } from '../../scripts/document.controller';
import { useCopilotWebSocket } from '../../hooks/useCopilotWebSocket';
import { createThread, getThreadMessages } from '../../services/copilot/copilot.service';
import { mapPersistedMessages } from '../../scripts/copilot.mapper';
import { usePdfViewer } from '../../contexts/PdfViewerContext';
import CopilotHeader from './CopilotHeader';
import CopilotMessages from './CopilotMessages';
import CopilotFooter from './CopilotFooter';
import CopilotDocumentPicker from './CopilotDocumentPicker';
import ThreadList from './ThreadList';
import { HEADER_HEIGHT } from '../../styles/layout';

interface CopilotPanelProps {
  isOpen: boolean;
  isPushMode: boolean;
  onClose: () => void;
}

const COPILOT_WIDTH = 400

const CopilotPanel: React.FC<CopilotPanelProps> = ({isOpen, isPushMode, onClose}) => {
  const {lang} = StateLanguage()
  const {open: openPdfViewer} = usePdfViewer()
  const [threadId, setThreadId] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [creating, setCreating] = useState(false)
  const [anchoredDocId, setAnchoredDocId] = useState<string | null>(null)
  const [anchoredDocName, setAnchoredDocName] = useState<string | null>(null)
  const [showDocPicker, setShowDocPicker] = useState(false)
  const [viewMode, setViewMode] = useState<'conversation' | 'history'>('conversation')
  const [loadingThread, setLoadingThread] = useState(false)

  const {
    status,
    messages,
    streaming,
    currentToolRunning,
    pendingAction,
    connectWS,
    disconnect,
    sendUserMessage,
    confirmAction,
    rejectAction,
    loadMessages,
    resetMessages,
  } = useCopilotWebSocket()

  // Connect/disconnect WS with open state
  useEffect(() => {
    if (isOpen) {
      connectWS()
    } else {
      disconnect()
    }
  }, [isOpen, connectWS, disconnect])

  // Listen for external open events (document anchoring handled here;
  // opening the drawer is handled by FloatingDrawer's own listener)
  useEffect(() => {
    return onOpenCopilot((params) => {
      if (params.documentId) {
        // Opened from /documents: anchor the doc + fresh thread
        setAnchoredDocId(params.documentId)
        setAnchoredDocName(params.documentName ?? null)
        setThreadId(null)
        resetMessages()
      } else {
        // Fresh open (FAB): clear anchor only, keep thread/messages
        setAnchoredDocId(null)
        setAnchoredDocName(null)
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Emit document:assigned when Copilot action succeeds with an anchored document
  const prevMsgCountRef = useRef(0)
  useEffect(() => {
    if (!anchoredDocId || messages.length <= prevMsgCountRef.current) {
      prevMsgCountRef.current = messages.length
      return
    }
    const newMessages = messages.slice(prevMsgCountRef.current)
    prevMsgCountRef.current = messages.length
    for (const msg of newMessages) {
      if (msg.role === 'system' && msg.actionSuccess) {
        emitDocumentAssigned(anchoredDocId)
        break
      }
    }
  }, [messages, anchoredDocId])

  const handleSend = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim()
    if (!text || streaming || creating) return

    if (!overrideText) setInput('')

    if (!threadId) {
      setCreating(true)
      try {
        const now = new Date()
        const title = `Conversation ${now.toLocaleDateString()} ${now.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}`
        const docIds = anchoredDocId ? [anchoredDocId] : undefined
        const thread = await createThread(title, docIds)
        setThreadId(thread.id)
        sendUserMessage(thread.id, text)
      } catch {
        resetMessages()
      } finally {
        setCreating(false)
      }
      return
    }

    sendUserMessage(threadId, text)
  }

  const handleSelectThread = async (selectedThreadId: string) => {
    setLoadingThread(true)
    try {
      const data = await getThreadMessages(selectedThreadId)
      const items = data?.items ?? data ?? []
      const mapped = mapPersistedMessages(items)
      loadMessages(mapped)
      setThreadId(selectedThreadId)
      setAnchoredDocId(null)
      setAnchoredDocName(null)
      setViewMode('conversation')
    } catch {
      resetMessages()
    } finally {
      setLoadingThread(false)
    }
  }

  const handleNewConversation = () => {
    setThreadId(null)
    setAnchoredDocId(null)
    setAnchoredDocName(null)
    resetMessages()
    setViewMode('conversation')
  }

  const handleToggleHistory = () => {
    setViewMode(prev => prev === 'history' ? 'conversation' : 'history')
  }

  // --- Panel content (identical for overlay and push) ---

  const panelContent = (
    <div className="card shadow-none rounded-0 w-100 h-100 d-flex flex-column">
      <CopilotHeader
        status={status}
        onClose={onClose}
        viewMode={viewMode}
        onToggleHistory={handleToggleHistory}
        onNewConversation={threadId ? handleNewConversation : undefined}
      />

      {viewMode === 'history' ? (
        <ThreadList onSelectThread={handleSelectThread} onNewConversation={handleNewConversation} />
      ) : loadingThread ? (
        <div className="d-flex align-items-center justify-content-center" style={{minHeight: 0, flex: '1 1 0'}}>
          <div className="text-center">
            <span className="spinner-border spinner-border-sm text-primary me-2"></span>
            <span className="text-muted fs-7">{lang.labels.copilotLoadingMessages}</span>
          </div>
        </div>
      ) : (
        <>
          {anchoredDocId && (
            <div className="d-flex align-items-center justify-content-between px-5 py-2 bg-light-info">
              <span
                className="fs-8 text-gray-700 text-truncate me-2 text-hover-primary cursor-pointer"
                role="button"
                title={lang.labels.viewDocument}
                onClick={() => openPdfViewer(anchoredDocId)}
              >
                <span className="svg-icon svg-icon-7 me-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="currentColor"/>
                    <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="currentColor"/>
                  </svg>
                </span>
                {anchoredDocName || lang.labels.copilotDocumentAttached}
              </span>
              {!threadId && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-active-light-danger flex-shrink-0"
                  style={{width: '20px', height: '20px'}}
                  title={lang.labels.copilotDetachDocument}
                  onClick={() => { setAnchoredDocId(null); setAnchoredDocName(null) }}
                >
                  <span className="svg-icon svg-icon-8">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="currentColor"/>
                      <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="currentColor"/>
                    </svg>
                  </span>
                </button>
              )}
            </div>
          )}

          {showDocPicker && (
            <CopilotDocumentPicker
              onAttach={(docId, docName) => { setAnchoredDocId(docId); setAnchoredDocName(docName); setShowDocPicker(false) }}
              onCancel={() => setShowDocPicker(false)}
            />
          )}

          <CopilotMessages
            messages={messages}
            streaming={streaming}
            currentToolRunning={currentToolRunning}
            pendingAction={pendingAction}
            onConfirm={confirmAction}
            onReject={rejectAction}
          />

          {!threadId && messages.length === 0 && (
            <div className="d-flex gap-2 flex-wrap px-5 pb-3">
              {anchoredDocId && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary"
                    disabled={streaming || creating || status !== 'open'}
                    onClick={() => handleSend(lang.labels.copilotSuggestMsgCreateExpense)}
                  >
                    <i className="bi bi-receipt me-1"></i>
                    {lang.labels.copilotSuggestCreateExpense}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light-info"
                    disabled={streaming || creating || status !== 'open'}
                    onClick={() => handleSend(lang.labels.copilotSuggestMsgCreateProviderInvoice)}
                  >
                    <i className="bi bi-file-earmark-text me-1"></i>
                    {lang.labels.copilotSuggestCreateProviderInvoice}
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-sm btn-light-success"
                disabled={streaming || creating || status !== 'open'}
                onClick={() => handleSend(lang.labels.copilotSuggestMsgProcessDocuments)}
              >
                <i className="bi bi-lightning me-1"></i>
                {lang.labels.copilotSuggestProcessDocuments}
              </button>
            </div>
          )}

          <CopilotFooter
            status={status}
            input={input}
            streaming={streaming}
            creating={creating}
            pendingAction={!!pendingAction}
            onInputChange={setInput}
            onSend={handleSend}
            onReconnect={connectWS}
            onAttachClick={() => setShowDocPicker(true)}
            showAttachButton={!threadId && !anchoredDocId && !showDocPicker}
          />
        </>
      )}
    </div>
  )

  // --- Envelope: push mode (sticky side panel) ---
  if (isPushMode) {
    if (!isOpen) return null
    return (
      <div
        className="d-flex flex-column border-start bg-white"
        style={{
          width: `${COPILOT_WIDTH}px`,
          flex: `0 0 ${COPILOT_WIDTH}px`,
          position: 'sticky',
          top: `${HEADER_HEIGHT}px`,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          transition: 'width 0.3s ease, flex 0.3s ease',
        }}
      >
        {panelContent}
      </div>
    )
  }

  // --- Envelope: overlay mode (drawer + backdrop, always in DOM for CSS transitions) ---
  return (
    <>
      <div id="drawer-copilot" className="bg-white drawer drawer-end" style={{width: '400px'}} data-kt-drawer="true">
        {panelContent}
      </div>
      <div id="overlay-copilot" onClick={onClose}></div>
    </>
  )
}

export default CopilotPanel;
